// Drive-Backup (Phase 8, Projektplan 11.3). Flaeche, die es im Original
// nicht gibt, deshalb frei gestaltet.
//
// "Verbinden" ist ein Link und kein fetch: Der Server antwortet mit einer
// Weiterleitung zu Google, und die muss der Browser selbst gehen (wie bei
// der Anmeldung, siehe seiten/Anmelden.tsx).

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fehlertext } from "../lib/api.js";
import {
  useBackupJetzt,
  useBackupListe,
  useBackupStatus,
  useBackupTrennen,
  useBackupWiederherstellen,
  type WiederherstellungsZeile,
} from "../lib/backup.js";
import type { Sammlung } from "../lib/me.js";
import { Hinweis, Karte, Knopf } from "../bausteine/Bausteine.js";
import { Unterseite } from "../bausteine/Unterseite.js";

const MELDUNGEN: Record<string, string> = {
  abgebrochen: "Die Verbindung wurde abgebrochen.",
  abgelaufen: "Der Versuch ist abgelaufen. Bitte noch einmal.",
  fehlgeschlagen: "Das Verbinden hat nicht geklappt. Bitte noch einmal.",
};

function datum(wert: string | null): string {
  if (!wert) return "noch nie";
  return new Date(wert).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Backup({ sammlungen }: { sammlungen: Sammlung[] }) {
  const [parameter] = useSearchParams();
  const meldung = MELDUNGEN[parameter.get("backup") ?? ""];
  const [ergebnis, setErgebnis] = useState<WiederherstellungsZeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const status = useBackupStatus();
  const liste = useBackupListe(Boolean(status.data?.verbunden));
  const jetzt = useBackupJetzt();
  const trennen = useBackupTrennen();
  const wiederherstellen = useBackupWiederherstellen();

  async function wiederherstellenAbsenden(fileId: string) {
    setFehler(null);
    setErgebnis(null);
    try {
      const antwort = await wiederherstellen.mutateAsync({
        fileId,
        targets: sammlungen.map((s) => s.id),
      });
      setErgebnis(antwort.sammlungen);
    } catch (e) {
      setFehler(fehlertext(e));
    }
  }

  return (
    <Unterseite
      titel="Drive-Backup"
      kinder={
        <>
          {parameter.get("backup") === "verbunden" && (
            <Hinweis art="gut" kinder="Verbunden. Das erste Backup läuft automatisch im Hintergrund." />
          )}
          {meldung && <Hinweis art="fehler" kinder={meldung} />}

          <Karte
            kinder={
              status.isPending ? (
                <p className="text-sm text-neutral-500">Einen Moment...</p>
              ) : status.data?.verbunden ? (
                <>
                  <h2 className="mb-1 text-base font-medium">Verbunden</h2>
                  <p className="mb-3 text-sm text-neutral-600">
                    Verbunden seit {datum(status.data.connectedAt)}. Letztes Backup:{" "}
                    {datum(status.data.lastBackupAt)}.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Knopf
                      onClick={() => jetzt.mutate()}
                      disabled={jetzt.isPending}
                      kinder="Jetzt sichern"
                    />
                    <Knopf
                      art="warnung"
                      onClick={() => trennen.mutate()}
                      disabled={trennen.isPending}
                      kinder="Trennen"
                    />
                  </div>
                  {jetzt.error && <Hinweis art="fehler" kinder={fehlertext(jetzt.error)} />}
                </>
              ) : (
                <>
                  <h2 className="mb-1 text-base font-medium">Nicht verbunden</h2>
                  <p className="mb-3 text-sm text-neutral-600">
                    Sichert deine Sammlungen regelmäßig in einen versteckten, nur für
                    Kennzeichensammler sichtbaren Ordner in deinem Google Drive. Kein Zugriff auf
                    sonstige Dateien.
                  </p>
                  <a
                    href="/api/backup/drive/connect"
                    className="inline-flex min-h-11 items-center rounded bg-akzent px-4 py-2 text-sm font-medium text-white transition hover:bg-akzent-dunkel"
                  >
                    Mit Google Drive verbinden
                  </a>
                </>
              )
            }
          />

          {status.data?.verbunden && (
            <Karte
              kinder={
                <>
                  <h2 className="mb-2 text-base font-medium">Vorhandene Backups</h2>
                  {liste.isPending && <p className="text-sm text-neutral-500">Einen Moment...</p>}
                  {liste.error && <Hinweis art="fehler" kinder={fehlertext(liste.error)} />}
                  {liste.data?.length === 0 && (
                    <p className="text-sm text-neutral-500">Noch kein Backup vorhanden.</p>
                  )}
                  <ul>
                    {liste.data?.map((datei) => (
                      <li
                        key={datei.id}
                        className="flex items-center justify-between border-t border-neutral-100 py-2 first:border-t-0"
                      >
                        <span className="text-sm text-neutral-700">{datum(datei.createdTime)}</span>
                        <Knopf
                          art="neben"
                          onClick={() => void wiederherstellenAbsenden(datei.id)}
                          disabled={wiederherstellen.isPending}
                          kinder="Wiederherstellen"
                        />
                      </li>
                    ))}
                  </ul>
                </>
              }
            />
          )}

          {fehler && <Hinweis art="fehler" kinder={fehler} />}

          {ergebnis && (
            <Karte
              kinder={
                <>
                  <h2 className="mb-2 text-base font-medium">Wiederhergestellt</h2>
                  {ergebnis.map((z) => (
                    <p key={z.collectionId} className="text-sm text-neutral-700">
                      <strong>{z.collectionName}</strong>: {z.importiert} neu, {z.bereitsVorhanden} waren
                      schon da
                    </p>
                  ))}
                </>
              }
            />
          )}
        </>
      }
    />
  );
}
